// Premium Avatar Utilities for Creators & Artists
// Generates unique, vibrant gradient backgrounds based on user identity

// Curated color palette for creators - vibrant, artistic, premium
const CREATOR_GRADIENTS = [
  ['#667eea', '#764ba2'], // Purple dream
  ['#f093fb', '#f5576c'], // Pink sunset
  ['#4facfe', '#00f2fe'], // Ocean blue
  ['#43e97b', '#38f9d7'], // Mint fresh
  ['#fa709a', '#fee140'], // Warm glow
  ['#a8edea', '#fed6e3'], // Soft pastel
  ['#ff9a9e', '#fecfef'], // Rose gold
  ['#ffecd2', '#fcb69f'], // Peach
  ['#a1c4fd', '#c2e9fb'], // Sky blue
  ['#d299c2', '#fef9d7'], // Lavender cream
  ['#89f7fe', '#66a6ff'], // Electric blue
  ['#cd9cf2', '#f6f3ff'], // Soft violet
  ['#fddb92', '#d1fdff'], // Golden hour
  ['#9890e3', '#b1f4cf'], // Aurora
  ['#ebc0fd', '#d9ded8'], // Misty rose
  ['#f6d365', '#fda085'], // Sunset orange
  ['#84fab0', '#8fd3f4'], // Fresh mint
  ['#cfd9df', '#e2ebf0'], // Silver mist
  ['#a6c0fe', '#f68084'], // Dusk
  ['#fccb90', '#d57eeb'], // Candy
];

// Generate a consistent hash from a string
const hashString = (str: string): number => {
  let hash = 0;
  const normalizedStr = (str || 'User').toLowerCase().trim();
  for (let i = 0; i < normalizedStr.length; i++) {
    const char = normalizedStr.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

// Get a unique gradient for a user based on their name/identifier
export const getAvatarGradient = (identifier: string): { gradient: string; colors: [string, string] } => {
  const hash = hashString(identifier);
  const index = hash % CREATOR_GRADIENTS.length;
  const colors = CREATOR_GRADIENTS[index] as [string, string];
  
  return {
    gradient: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`,
    colors
  };
};

// Get initials from a name (up to 2 characters)
export const getInitials = (name: string): string => {
  if (!name) return '?';
  
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    // Single word - return first letter
    return parts[0].charAt(0).toUpperCase();
  }
  // Multiple words - return first letter of first and last
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Get complete avatar styles for MUI Avatar component
export const getAvatarStyles = (
  identifier: string, 
  size: number | { xs?: number; sm?: number; md?: number } = 56
) => {
  const { gradient } = getAvatarGradient(identifier);
  
  // Handle responsive sizes
  const sizeStyles = typeof size === 'number' 
    ? { width: size, height: size, fontSize: size * 0.4 }
    : { 
        width: size, 
        height: size, 
        fontSize: { 
          xs: (size.xs || 48) * 0.4, 
          sm: (size.sm || size.xs || 56) * 0.4,
          md: (size.md || size.sm || size.xs || 56) * 0.4
        }
      };

  return {
    ...sizeStyles,
    background: gradient,
    fontWeight: 700,
    letterSpacing: '0.5px',
    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
    boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
    border: '2px solid rgba(255,255,255,0.2)',
  };
};

export default { getAvatarGradient, getInitials, getAvatarStyles };
